import { NgClass } from '@angular/common';
import { Component, Input, OnInit, forwardRef } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgxGpAutocompleteModule } from '@angular-magic/ngx-gp-autocomplete';

import { US_STATES } from 'src/app/core/constants/states';
import { DPFormField } from 'src/app/core/models/DPFormField';
import { DPFormFieldType } from 'src/app/core/models/enums/DPFormFieldType';
import { DPValidationType } from 'src/app/core/models/enums/DPValidationType';
import { FormParserService } from 'src/app/core/services/form-parser.service';

import { ItemClassPipe } from 'src/app/shared/pipes/item-class.pipe';
import { FieldErrorComponent } from '../../field-error/field-error.component';
import { DynamicFieldComponent } from '../../dynamic-field/dynamic-field.component';
import { FormControlPipe } from 'src/app/shared/pipes/form-control.pipe';

/**
 * Address field component.
 *
 * The value is JSON string of following object:
 *
 * { "street": "", "city": "", "state": "", "postalCode": "" }
 *
 */
@Component({
  selector: 'app-address',
  standalone: true,
  imports: [
    NgClass,
    ReactiveFormsModule,
    NgxGpAutocompleteModule,
    FieldErrorComponent,
    ItemClassPipe,
    FormControlPipe,
    forwardRef(() => DynamicFieldComponent),
  ],
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
})
export class AddressComponent implements OnInit {
  @Input() field: DPFormField;

  @Input() control: UntypedFormControl;

  @Input() disabled: boolean;

  // Mark as touched event from parent form
  @Input() markAsTouched: Subject<void>;

  // US states list
  states = US_STATES;

  // Address Form
  form: UntypedFormGroup;

  // Fields for address form
  subfields: DPFormField[];

  private unsubscribe$ = new Subject<void>();

  options: google.maps.places.AutocompleteOptions = {
    componentRestrictions: { country: ['US', 'CA'] },
    fields: ['address_components', 'formatted_address'],
  };

  constructor(private formParser: FormParserService) {}

  ngOnInit(): void {
    /**
     * Address form fields
     */
    this.subfields = [
      {
        type: DPFormFieldType.SingleLine,
        label: 'Address',
        name: 'street',
        colSpan: {
          desktop: 12,
          tablet: 12,
          mobile: 12,
        },
        required: this.field.required,
      },
      {
        type: DPFormFieldType.SingleLine,
        label: 'City',
        name: 'city',
        colSpan: {
          desktop: 6,
          tablet: 6,
          mobile: 6,
        },
        required: this.field.required,
        validations: [
          {
            validationTypeId: DPValidationType.MaxLength,
            value: '100',
            message: 'City is too long.',
            pageQuestionId: null,
            pageQuestionLookupId: null,
            pattern: null,
          },
        ],
      },
      {
        type: DPFormFieldType.Dropdown,
        label: 'State',
        name: 'state',
        colSpan: {
          desktop: 2,
          tablet: 2,
          mobile: 2,
        },
        required: this.field.required,
        options: US_STATES.map((st) => ({
          key: st.abbreviation,
          value: st.abbreviation,
        })),
        optionKeyOnly: true,
      },
      {
        type: DPFormFieldType.SingleLine,
        label: 'Postal Code',
        name: 'postalCode',
        colSpan: {
          desktop: 4,
          tablet: 4,
          mobile: 4,
        },
        required: this.field.required,
      },
    ];

    // Create address form
    this.form = this.formParser.createDynamicForm(this.subfields, {}, true);

    // Update address form values.
    // This will populate value from parent form.
    this.patchValue();

    // When address form value changes, it updates control value.
    this.form.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((values) => {
        this.customFieldValidations();

        if (this.control) {
          this.control.setValue(JSON.stringify(values));
        }
      });

    if (this.markAsTouched) {
      /**
       * When parent form passes down markAsTouched event, mark all address form field as touched.
       */
      this.markAsTouched.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
        this.form.markAllAsTouched();
      });
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Event handler when you select one of the suggesstions
   */
  handleAddressChange(address: google.maps.places.PlaceResult) {
    const components = address.address_components ?? [];

    const state = components.find((component) =>
      component.types.includes('administrative_area_level_1')
    );
    const city = components.find(
      (component) =>
        component.types.includes('locality') ||
        component.types.includes('sublocality')
    );
    const postalCode = components.find((component) =>
      component.types.includes('postal_code')
    );
    const street = address.formatted_address
      ? address.formatted_address.split(', ').slice(0, -3).join(', ')
      : '';

    this.form.patchValue({
      street,
      city: city?.long_name,
      state: state?.short_name,
      postalCode: postalCode?.long_name,
    });
  }

  /**
   * Populates form field value from form control to address form.
   */
  private patchValue() {
    if (!this.field || !this.control) {
      return;
    }

    let values: any;
    try {
      values = JSON.parse(this.control.value || '{}');
    } catch (err) {
      values = {};
    }

    this.form.patchValue(values);
  }

  /**
   * Additional field validations.
   *
   * This validates postalCode field.
   */
  private customFieldValidations() {
    const values = this.form.value;

    if (
      values.postalCode &&
      !values.postalCode.match(/(^\d{5}$)|(^\d{9}$)|(^\d{5}-\d{4}$)/) &&
      !values.postalCode.match(
        /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/
      )
    ) {
      this.form.get('postalCode').setErrors({ custom: 'Invalid code' });
      this.control.setErrors({ custom: 'Invalid postal code' });
    }
  }
}
